import React, { FC, HTMLAttributes, ReactNode, useCallback } from 'react';

import { useIntl } from 'react-intl';
import styled, { css, useTheme } from 'styled-components';

import { ClickableContainer } from 'components/clickable-container';
import { brandFont } from 'components/layout/brand-font';
import { LinkButton } from 'components/link-button';
import { Arrow } from 'components/selection-container/arrow';
import VisuallyHidden from 'components/visually-hidden';
import useOnClickOutside from 'hooks/use-on-click-outside';
import { useTabNavigationWatcher } from 'state/accessibility/use-tab-navigation-watcher';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';
import { isButtonLikeKeypress } from 'utils/event';
import { filterProps } from 'utils/filter-props';
import { screenReaderOnly } from 'utils/style/screen-reader-only';

import { RewardsStyleVariant, WithStyleVariant } from '../types';

import { CategoryContainer } from './category-container';

type CategoryDropdownProps = {
  children: ReactNode;
  focusOptionsWhenNotVisible?: boolean;
  id: string;
  label: string;
  onClickSeeMoreActions?: () => void;
  optionsVisible: boolean;
  overlay?: boolean;
  selectedOptionName?: string;
  title: ReactNode;
  toggleOptionsVisible: () => void;
  handleOutsideClick?: () => void;
} & WithStyleVariant &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const noBottomBorder = css`
  border-bottom: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;
const CategoryDropdownContainer = styled(CategoryContainer)<
  Pick<CategoryDropdownProps, 'optionsVisible' | 'overlay'>
>`
  position: relative;
  width: 100%;
  padding: 0;
  ${({ optionsVisible, overlay }) => overlay && optionsVisible && noBottomBorder};
`;

const TitleContainer = styled.div`
  padding: ${primitive.$spacing2} ${primitive.$spacing4} 0;
  width: 100%;
`;

const Title = styled(ClickableContainer)<
  { isUsingTabNavigation: boolean; showBorder: boolean } & WithStyleVariant
>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${({ showBorder }) => (showBorder ? '1px' : '0px')};
  border-bottom-style: solid;
  border-bottom-color: ${({ variant, theme }) =>
    variant === RewardsStyleVariant.Secondary
      ? primitive.$blackOpacity30
      : theme.token('border-color-pattern-default')};
  &: focus {
    ${({ isUsingTabNavigation }) => !isUsingTabNavigation && 'outline: none;'};
  }
  width: 100%;
  padding-bottom: ${primitive.$spacing2};

  & > {
    height: 3rem;
  }
`;

type OptionsContainerProps = Pick<
  CategoryDropdownProps,
  'focusOptionsWhenNotVisible' | 'optionsVisible' | 'overlay'
> &
  WithStyleVariant;

const overlayStyle = css<OptionsContainerProps>`
  position: absolute;
  width: calc(100% + 2px);
  left: -1px;
  background: ${({ variant, theme }) =>
    variant === RewardsStyleVariant.Secondary
      ? primitive.th.$alwaysRedDarken10
      : theme.token('background-pattern')};
  z-index: ${Styles.zIndex.below};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ variant, theme }) =>
    variant === RewardsStyleVariant.Secondary
      ? primitive.th.$alwaysRedDarken10
      : theme.token('border-color-pattern-default')};
  border-top: none;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
`;

const OptionsContainer = styled.div<OptionsContainerProps>`
  ${({ overlay }) => overlay && overlayStyle}
  padding: 0 ${primitive.$spacing4};
  z-index: ${Styles.zIndex.normal};
  ${({ focusOptionsWhenNotVisible, optionsVisible }) =>
    !optionsVisible && (focusOptionsWhenNotVisible ? screenReaderOnly : 'display: none;')};
`;

const StyledArrow = styled(Arrow)`
  margin-right: ${primitive.$spacing2};
  margin-left: ${primitive.$spacing2};
`;

const SeeMoreOptionsCTA = styled(filterProps(LinkButton, 'variant'))<WithStyleVariant>`
  width: 100%;
  padding: ${primitive.$spacing6} 0 ${primitive.$spacing7};
  text-align: left;
  font: ${brandFont.copyOne};
  color: ${({ variant, theme }) =>
    variant === RewardsStyleVariant.Secondary
      ? theme.token('text-link-reversed')
      : theme.token('text-link-default')};
`;

// TODO: consolidate with other solutions (TextExpander, SelectionContainer) in the code base, extract fieldset
export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  children,
  focusOptionsWhenNotVisible = false,
  id,
  label,
  onClickSeeMoreActions,
  optionsVisible,
  overlay = false,
  selectedOptionName,
  title,
  toggleOptionsVisible,
  variant,
  handleOutsideClick,
  ...props
}) => {
  const { isUsingTabNavigation } = useTabNavigationWatcher();
  const OutsideClickWrapper = useOnClickOutside(() => {
    if (handleOutsideClick) {
      handleOutsideClick();
    }
  });

  const loyalty2SeeMoreOptionsEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_LOYALTY_2_0_SEE_MORE_OPTIONS
  );
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isButtonLikeKeypress(e)) {
        e.preventDefault();
        toggleOptionsVisible();
      }
    },
    [toggleOptionsVisible]
  );

  return (
    <OutsideClickWrapper>
      <CategoryDropdownContainer
        optionsVisible={optionsVisible}
        overlay={overlay}
        variant={variant}
      >
        <TitleContainer>
          <Title
            aria-controls={`${id}-content`}
            aria-expanded={optionsVisible}
            isUsingTabNavigation={isUsingTabNavigation}
            onClick={toggleOptionsVisible}
            onKeyDown={onKeyDown}
            showBorder={optionsVisible}
            tabIndex={0}
            variant={variant}
          >
            <VisuallyHidden>
              {formatMessage(
                { id: 'rewardCategory' },
                {
                  categoryName: label,
                  selectedOptionText: selectedOptionName
                    ? formatMessage(
                        { id: 'containsSelectedOption' },
                        {
                          rewardTier: selectedOptionName,
                        }
                      )
                    : '',
                }
              )}
            </VisuallyHidden>
            <span aria-hidden>{title}</span>
            <StyledArrow
              aria-hidden
              isOpen={optionsVisible}
              fill={
                variant === RewardsStyleVariant.Secondary
                  ? theme.token('icon-reversed')
                  : theme.token('icon-default')
              }
            />
          </Title>
        </TitleContainer>
        <OptionsContainer
          {...props}
          id={`${id}-content`}
          focusOptionsWhenNotVisible={focusOptionsWhenNotVisible}
          optionsVisible={optionsVisible}
          overlay={overlay}
          variant={variant}
        >
          {children}
          {loyalty2SeeMoreOptionsEnabled && onClickSeeMoreActions && optionsVisible && (
            <SeeMoreOptionsCTA onClick={onClickSeeMoreActions} variant={variant}>
              {formatMessage({ id: 'seeMoreOptions' })}
            </SeeMoreOptionsCTA>
          )}
        </OptionsContainer>
      </CategoryDropdownContainer>
    </OutsideClickWrapper>
  );
};
